import { ChangeEvent } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DCUIContent, DCUIContentAcuant } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { DCUIThemingTextContentValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingTextContentBrowserWarningsSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    dcuiContent: DCUIContent;
    inheritedDCUIContent: DCUIContent;
    pageLevel: PageLevelTypes;
    validationErrors: DCUIThemingTextContentValidationErrors;
    updateDCUIContentPropertyValue: (dcuiContent: DCUIContent) => void;
};

export const DCUIThemingTextContentBrowserWarningsSection = (props: DCUIThemingTextContentBrowserWarningsSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        dcuiContent,
        inheritedDCUIContent,
        pageLevel,
        validationErrors,
        updateDCUIContentPropertyValue
    } = props;

    const updateDCUIContentBrowserWarningsTextFieldValue = (
        key: keyof DCUIContentAcuant,
        value: string
    ) => {
        const newPropertyValue: DCUIContent = {
            ...dcuiContent,
            acuant: {
                ...dcuiContent.acuant,
                [key]: value,
            },
        };
        updateDCUIContentPropertyValue(newPropertyValue);
    };

    const handleBrowserWarningsTextIosSafariChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentBrowserWarningsTextFieldValue("iosSafariBrowserWarningText", event.target.value);
    };

    const handleResetBrowserWarningsTextIosSafari = () => {
        updateDCUIContentBrowserWarningsTextFieldValue("iosSafariBrowserWarningText", inheritedDCUIContent.acuant.iosSafariBrowserWarningText);
    };

    const handleBrowserWarningsTextDesktopSafariChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentBrowserWarningsTextFieldValue("desktopSafariBrowserWarningText", event.target.value);
    };

    const handleResetBrowserWarningsTextDesktopSafari = () => {
        updateDCUIContentBrowserWarningsTextFieldValue("desktopSafariBrowserWarningText", inheritedDCUIContent.acuant.desktopSafariBrowserWarningText);
    };

    const handleBrowserWarningsTextAndroidChromeChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentBrowserWarningsTextFieldValue("androidChromeBrowserWarningText", event.target.value);
    };

    const handleResetBrowserWarningsTextAndroidChrome = () => {
        updateDCUIContentBrowserWarningsTextFieldValue("androidChromeBrowserWarningText", inheritedDCUIContent.acuant.androidChromeBrowserWarningText);
    };

    const handleBrowserWarningsDesktopChromeChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentBrowserWarningsTextFieldValue("desktopChromeBrowserWarningText", event.target.value);
    };

    const handleResetBrowserWarningsTextDesktopChrome = () => {
        updateDCUIContentBrowserWarningsTextFieldValue("desktopChromeBrowserWarningText", inheritedDCUIContent.acuant.desktopChromeBrowserWarningText);
    };

    const browserWarningsResources = resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.textContent.sections.browserWarnings;

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.iosSafariBrowserWarningText}
                currentThemingPropertyValue={dcuiContent.acuant.iosSafariBrowserWarningText}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={"document-capture-text-content-ios-safari-input"}
                    automationId={"document-capture-text-content-ios-safari-input"}
                    label={browserWarningsResources.inputs.iosSafariBrowserWarningText.label}
                    ariaLabel={browserWarningsResources.inputs.iosSafariBrowserWarningText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleBrowserWarningsTextIosSafariChange}
                    value={dcuiContent.acuant.iosSafariBrowserWarningText || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={browserWarningsResources.inputs.iosSafariBrowserWarningText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetBrowserWarningsTextIosSafari}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.iosSafariBrowserWarningTextError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.desktopSafariBrowserWarningText}
                currentThemingPropertyValue={dcuiContent.acuant.desktopSafariBrowserWarningText}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={"document-capture-text-content-desktop-safari-input"}
                    automationId={"document-capture-text-content-desktop-safari-input"}
                    label={browserWarningsResources.inputs.desktopSafariBrowserWarningText.label}
                    ariaLabel={browserWarningsResources.inputs.desktopSafariBrowserWarningText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleBrowserWarningsTextDesktopSafariChange}
                    value={dcuiContent.acuant.desktopSafariBrowserWarningText || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={browserWarningsResources.inputs.desktopSafariBrowserWarningText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetBrowserWarningsTextDesktopSafari}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.desktopSafariBrowserWarningTextError}

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.androidChromeBrowserWarningText}
                currentThemingPropertyValue={dcuiContent.acuant.androidChromeBrowserWarningText}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={"document-capture-text-content-android-chrome-input"}
                    automationId={"document-capture-text-content-android-chrome-input"}
                    label={browserWarningsResources.inputs.androidChromeBrowserWarningText.label}
                    ariaLabel={browserWarningsResources.inputs.androidChromeBrowserWarningText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleBrowserWarningsTextAndroidChromeChange}
                    value={dcuiContent.acuant.androidChromeBrowserWarningText || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={browserWarningsResources.inputs.androidChromeBrowserWarningText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetBrowserWarningsTextAndroidChrome}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.androidChromeBrowserWarningTextError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.desktopChromeBrowserWarningText}
                currentThemingPropertyValue={dcuiContent.acuant.desktopChromeBrowserWarningText}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={"document-capture-text-content-desktop-chrome-input"}
                    automationId={"document-capture-text-content-desktop-chrome-input"}
                    label={browserWarningsResources.inputs.desktopChromeBrowserWarningText.label}
                    ariaLabel={browserWarningsResources.inputs.desktopChromeBrowserWarningText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleBrowserWarningsDesktopChromeChange}
                    value={dcuiContent.acuant.desktopChromeBrowserWarningText || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={browserWarningsResources.inputs.desktopChromeBrowserWarningText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetBrowserWarningsTextDesktopChrome}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.desktopChromeBrowserWarningTextError}
        </div>
    );
};